import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Pagination,
  CircularProgress,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Add,
  Refresh,
  FilterList,
  Warning,
  Error as ErrorIcon,
  Info,
  Assignment,
  Today,
  Pending,
  Done,
  Build,
} from '@mui/icons-material';
import dayjs from 'dayjs';

import { useSiteContext } from '../../hooks/useSiteContext';
import { useAuth } from '../../hooks/useAuth';
import stationLogService from '../../services/stationLogService';
import {
  StationLogEntry,
  StationLogEntrySummary,
  StationLogSummary,
  StationLogFilters,
  StationLogEntryType,
  StationLogPriority,
  CreateStationLogEntry,
  ShiftHandover,
} from '../../types/stationLog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`station-log-tabpanel-${index}`}
      aria-labelledby={`station-log-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const StationLogDashboard: React.FC = () => {
  const { currentSite, isInSiteContext } = useSiteContext();
  const { user } = useAuth();
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  // Data state
  const [entries, setEntries] = useState<StationLogEntrySummary[]>([]);
  const [summary, setSummary] = useState<StationLogSummary | null>(null);
  const [recentEntries, setRecentEntries] = useState<StationLogEntrySummary[]>([]);
  const [pendingActions, setPendingActions] = useState<StationLogEntry[]>([]);
  const [currentShift, setCurrentShift] = useState<ShiftHandover | null>(null);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filter and pagination state
  const [filters, setFilters] = useState<StationLogFilters>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateStationLogEntry>({
    entry_type: 'operational',
    priority: 'medium',
    title: '',
    description: '',
    location: '',
            event_time: dayjs().format("YYYY-MM-DDTHH:mm"),
    requires_action: false,
    action_required: '',
    tags: '',
  });

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load summary and recent entries in parallel
      const [summaryData, recentData, pendingData] = await Promise.all([
        stationLogService.getSummary(filters),
        stationLogService.getRecentEntries(),
        stationLogService.getPendingActions(),
      ]);

      setSummary(summaryData);
      setRecentEntries(recentData);
      setPendingActions(pendingData);

      // Load entries for current tab
      if (currentTab === 0) {
        const entriesData = await stationLogService.getEntries(filters, page);
        setEntries(entriesData.results as StationLogEntrySummary[]);
        setTotalPages(Math.ceil(entriesData.count / 20)); // Assuming 20 per page
      }

      // Try to load current shift (may not exist)
      try {
        const shiftData = await stationLogService.getCurrentShift();
        setCurrentShift(shiftData);
      } catch (shiftError) {
        // No current shift is okay
        setCurrentShift(null);
      }

    } catch (err) {
      console.error('Failed to load station log data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load station log data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load data on component mount and when filters change
  useEffect(() => {
    if (isInSiteContext) {
      loadDashboardData();
    }
  }, [isInSiteContext, loadDashboardData]);

  const handleCreateEntry = async () => {
    try {
      setSubmitting(true);
      await stationLogService.createEntry(createForm);
      
      // Reset form and close dialog
      setCreateForm({
        entry_type: 'operational',
        priority: 'medium',
        title: '',
        description: '',
        location: '',
        event_time: dayjs().format("YYYY-MM-DDTHH:mm"),
        requires_action: false,
        action_required: '',
        tags: '',
      });
      setCreateDialogOpen(false);
      
      // Reload data
      loadDashboardData();
      
    } catch (err) {
      console.error('Failed to create entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledgeEntry = async (entryId: number) => {
    try {
      await stationLogService.acknowledgeEntry(entryId);
      loadDashboardData(); // Reload to update status
    } catch (err) {
      console.error('Failed to acknowledge entry:', err);
      setError(err instanceof Error ? err.message : 'Failed to acknowledge entry');
    }
  };

  const handleCompleteAction = async (entryId: number) => {
    try {
      await stationLogService.completeAction(entryId);
      loadDashboardData(); // Reload to update status
    } catch (err) {
      console.error('Failed to complete action:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete action');
    }
  };

  const getPriorityColor = (priority: StationLogPriority) => {
    switch (priority) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: StationLogEntryType) => {
    switch (type) {
      case 'operational': return <Assignment />;
      case 'maintenance': return <Build />;
      case 'safety': return <Warning />;
      case 'permit': return <Assignment />;
      case 'emergency': return <ErrorIcon />;
      case 'environmental': return <Info />;
      case 'system': return <Info />;
      default: return <Info />;
    }
  };

  if (!isInSiteContext) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          No site selected. Please select a site from the dashboard to view station log.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Station Log - {currentSite.siteName}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Real-time operations log and event tracking
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDashboardData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Entry
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">{summary.total_entries}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Entries</Typography>
                  </Box>
                  <Assignment color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">{summary.recent_entries_24h}</Typography>
                    <Typography variant="body2" color="text.secondary">Last 24 Hours</Typography>
                  </Box>
                  <Today color="info" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">{summary.pending_actions}</Typography>
                    <Typography variant="body2" color="text.secondary">Pending Actions</Typography>
                  </Box>
                  <Pending color="warning" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">{summary.completed_actions}</Typography>
                    <Typography variant="body2" color="text.secondary">Completed Actions</Typography>
                  </Box>
                  <Done color="success" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter Panel */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Entry Type</InputLabel>
                  <Select
                    value={filters.entry_type || ''}
                    label="Entry Type"
                    onChange={(e) => setFilters({...filters, entry_type: e.target.value as StationLogEntryType})}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="operational">Operational</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="safety">Safety</MenuItem>
                    <MenuItem value="permit">Permit Related</MenuItem>
                    <MenuItem value="environmental">Environmental</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                    <MenuItem value="system">System Event</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={filters.priority || ''}
                    label="Priority"
                    onChange={(e) => setFilters({...filters, priority: e.target.value as StationLogPriority})}
                  >
                    <MenuItem value="">All Priorities</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="From Date"
                  type="date"
                  value={filters.event_date_gte || ''}
                  onChange={(e) => setFilters({...filters, event_date_gte: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="To Date"
                  type="date"
                  value={filters.event_date_lte || ''}
                  onChange={(e) => setFilters({...filters, event_date_lte: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.requires_action === true}
                      onChange={(e) => setFilters({...filters, requires_action: e.target.checked ? true : undefined})}
                    />
                  }
                  label="Requires Action"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={filters.search || ''}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  placeholder="Search title, description, tags..."
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={() => setPage(1)} // Reset to first page when filtering
                size="small"
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({});
                  setPage(1);
                }}
                size="small"
              >
                Clear Filters
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="Recent Entries" />
          <Tab label="Pending Actions" />
          <Tab label="Today's Entries" />
          <Tab label="Current Shift" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={currentTab} index={0}>
        {/* Recent Entries */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Entry #</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Logged By</TableCell>
                    <TableCell>Event Time</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentEntries.map((entry) => (
                    <TableRow key={entry.id} hover>
                      <TableCell>{entry.entry_number}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getTypeIcon(entry.entry_type)}
                          {entry.entry_type_display}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={entry.priority_display} 
                          color={getPriorityColor(entry.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{entry.title}</TableCell>
                      <TableCell>{entry.location}</TableCell>
                      <TableCell>{entry.logged_by_name}</TableCell>
                      <TableCell>
                        {dayjs(entry.event_time).format('MMM DD, YYYY HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {entry.requires_action && (
                            <Chip
                              label={entry.action_completed ? "Action Complete" : "Action Required"}
                              color={entry.action_completed ? "success" : "warning"}
                              size="small"
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Pending Actions */}
        <Grid container spacing={2}>
          {pendingActions.map((entry) => (
            <Grid item xs={12} md={6} key={entry.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6">{entry.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {entry.entry_number} • {entry.entry_type_display}
                      </Typography>
                    </Box>
                    <Chip 
                      label={entry.priority_display} 
                      color={getPriorityColor(entry.priority)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" paragraph>
                    {entry.description}
                  </Typography>
                  
                  {entry.action_required && (
                    <Box bgcolor="warning.light" p={2} borderRadius={1} mb={2}>
                      <Typography variant="subtitle2" color="warning.dark">
                        Action Required:
                      </Typography>
                      <Typography variant="body2" color="warning.dark">
                        {entry.action_required}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(entry.event_time).format('MMM DD, YYYY HH:mm')}
                    </Typography>
                    <Box display="flex" gap={1}>
                      {!entry.acknowledged_by && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleAcknowledgeEntry(entry.id)}
                        >
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleCompleteAction(entry.id)}
                      >
                        Complete Action
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {/* Today's Entries - Similar to recent entries but filtered for today */}
        <Typography variant="h6" gutterBottom>
          Today's Station Log Entries
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          All entries logged today at {currentSite.siteName}
        </Typography>
        {/* TODO: Implement today's entries table */}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {/* Current Shift */}
        {currentShift ? (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Shift Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Shift Type:</Typography>
                  <Typography variant="body1">{currentShift.shift_type_display}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date:</Typography>
                  <Typography variant="body1">{dayjs(currentShift.shift_date).format('MMM DD, YYYY')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Operational Status:</Typography>
                  <Typography variant="body1">{currentShift.operational_status}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Active Permits:</Typography>
                  <Typography variant="body1">{currentShift.active_permits}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Ongoing Work:</Typography>
                  <Typography variant="body1">{currentShift.ongoing_work}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ) : (
          <Alert severity="info">
            No current shift information available.
          </Alert>
        )}
      </TabPanel>

      {/* Create Entry Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Station Log Entry</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Entry Type</InputLabel>
                <Select
                  value={createForm.entry_type}
                  label="Entry Type"
                  onChange={(e) => setCreateForm({...createForm, entry_type: e.target.value as StationLogEntryType})}
                >
                  <MenuItem value="operational">Operational</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="safety">Safety</MenuItem>
                  <MenuItem value="permit">Permit Related</MenuItem>
                  <MenuItem value="environmental">Environmental</MenuItem>
                  <MenuItem value="emergency">Emergency</MenuItem>
                  <MenuItem value="system">System Event</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={createForm.priority}
                  label="Priority"
                  onChange={(e) => setCreateForm({...createForm, priority: e.target.value as StationLogPriority})}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={createForm.title}
                onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={createForm.location}
                onChange={(e) => setCreateForm({...createForm, location: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Event Time"
                type="datetime-local"
                value={createForm.event_time}
                onChange={(e) => setCreateForm({...createForm, event_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createForm.requires_action}
                    onChange={(e) => setCreateForm({...createForm, requires_action: e.target.checked})}
                  />
                }
                label="Requires Action"
              />
            </Grid>
            {createForm.requires_action && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Action Required"
                  value={createForm.action_required}
                  onChange={(e) => setCreateForm({...createForm, action_required: e.target.value})}
                  multiline
                  rows={2}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags"
                value={createForm.tags}
                onChange={(e) => setCreateForm({...createForm, tags: e.target.value})}
                placeholder="Comma-separated tags"
                helperText="Enter tags separated by commas"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateEntry} 
            variant="contained"
            disabled={!createForm.title || !createForm.description || submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Create Entry'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StationLogDashboard; 